import { useEffect, useMemo, useState } from 'react';
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef } from '@mui/x-data-grid-pro';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { useFetchDataQuery, useGetMealCardBalanceMutation, useMealCardRefundMutation } from '../../services/services';
import Table from '../../../../components/table/Table';
import { usePermission } from '../../../../hooks/usePermission';
import { Box, Card, Typography, TextField } from '@mui/material';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';
import CustomBtn from '../../../../components/customBtn/CustomBtn';
import { enqueueSnackbar } from 'notistack';

import { Security as SecurityIcon, History } from '@mui/icons-material';

const MealCardsList = () => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({});
  const [selectedCardForRefund, setSelectedCardForRefund] = useState<any>(null);
  const [cardBalance, setCardBalance] = useState<string>('');
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState<boolean>(false);
  const [refundAmountError, setRefundAmountError] = useState<string>('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [isRefundProcessing, setIsRefundProcessing] = useState<boolean>(false);
  const { hasPermission: canViewCardList, isLoading: isLoadingPermission } = usePermission({
    code: 'Corporate.POST.Reading.KartListeleme(YemekKartı)',
  });
  const { hasPermission: canRefundMealCard, isLoading: isRefundPermissionLoading } = usePermission({
    code: 'Corporate.POST.Reading.YemekKartıİadesi',
  });
  const { hasPermission: canViewMealCardBalance, isLoading: isBalancePermissionLoading } = usePermission({
    code: 'Corporate.POST.Reading.YemekKartıBakiyesiGörüntüleme',
  });
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const listUrl = `/Card/CustomerMealCardsV2?customerId=${currentUserState?.companyId}`;

  const customStatusCellTemplate = (params: any) => {
    const data = params.value || params.row?.status;
    if (data === 'A') {
      return 'Açık Kart';
    }
    if (data === 'T') {
      return 'Geçici Kapalı';
    }
    if (data === 'I') {
      return 'İptal Kart';
    }
    if (data === 'R') {
      return 'Risk';
    }
    if (data === 'S') {
      return 'Stoplist';
    }
    if (data === 'K') {
      return 'Bloke Kart';
    }
    if (data === 'B') {
      return 'Bekleyen Kart';
    }
    if (data === 'V') {
      return 'Süresi Dolmuş';
    }
    if (data === 'F') {
      return 'Fraud';
    }
    return data || '';
  };

  // API hooks
  const [getMealCardBalance] = useGetMealCardBalanceMutation();
  const [mealCardRefund] = useMealCardRefundMutation();

  const columns: GridColDef[] = useMemo(() => [
    { field: 'customerName', headerName: 'Kullanıcı Ad', width: 250 },
    { field: 'cardNo', headerName: 'Kart Numarası', width: 222 },
    {
      field: 'status',
      headerName: 'Durumu',
      width: 200,
      type: 'singleSelect',
      valueOptions: [
        { value: 'A', label: 'Açık Kart' },
        { value: 'T', label: 'Geçici Kapalı' },
        { value: 'I', label: 'İptal Kart' },
        { value: 'R', label: 'Risk' },
        { value: 'S', label: 'Stoplist' },
        { value: 'K', label: 'Bloke Kart' },
        { value: 'B', label: 'Bekleyen Kart' },
        { value: 'V', label: 'Süresi Dolmuş' },
        { value: 'F', label: 'Fraud' }
      ],
      renderCell: (params: any) => customStatusCellTemplate(params)
    },
    { field: 'productCode', headerName: 'Ürün Kodu', width: 200 },
    { field: 'walletNo', headerName: 'Hesap Numarası', width: 200 },
    { field: 'idNo', headerName: 'TCKN', width: 150 },
    { field: 'phoneNumber', headerName: 'Telefon Numarası', width: 150 },
    {
      field: 'refund',
      headerName: 'İade Al',
      width: 100,
      renderCell: (params: any) => {
        // Yetkiler cevaplanmadan butonu gösterme
        if (isRefundPermissionLoading || isBalancePermissionLoading) {
          return null;
        }
        if (!canRefundMealCard) {
          return null;
        }

        const customerId = params.row.customerId;

        // customerId kontrolü - sadece geçerli customerId varsa butonu göster
        if (customerId === 0 || customerId === null || customerId === '') {
          return null;
        }

        return (
          <CustomBtn
            key={`refund-${params.row.id}`}
            name="İade Al"
            onclick={() => handleRefundClick(params)}
          />
        );
      },
      filterable: false,
      sortable: false,
    },
  ], [canRefundMealCard, isRefundPermissionLoading, isBalancePermissionLoading]);

  const [requestBody, setRequestBody] = useState({
    filters: [],
    pageSize: 25,
    page: 1,
    orderBy: '',
    orderDesc: false,
    customerId: currentUserState?.companyId || undefined,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Kurum değişince istek body'yi yeni companyId ile senkronla (aksi halde eski customerId ile kalabiliyor)
  useEffect(() => {
    if (!currentUserState?.companyId) return;
    setPaginationModel({ page: 0, pageSize: 25 });
    setRequestBody((prev) => ({
      ...prev,
      filters: [],
      pageSize: 25,
      page: 1,
      orderBy: '',
      orderDesc: false,
      customerId: currentUserState.companyId,
    }));
  }, [currentUserState?.companyId]);
  const {
    data: fetchData,
    isFetching,
    error: fetchError,
    isLoading: isFetchLoading,
  } = useFetchDataQuery(
    { url: listUrl, filter: requestBody, method: 'POST' },
    { skip: !requestBody?.customerId || !canViewCardList }
  );

  // İade Al butonuna tıklandığında çalışacak fonksiyon
  const handleRefundClick = async (params: any) => {
    setSelectedCardForRefund(params.row);
    setIsModalOpen(true);
    setRefundAmount('');
    setRefundAmountError('');

    // Kart bakiyesini getir - yetkisi varsa
    if (!isBalancePermissionLoading && canViewMealCardBalance) {
      await fetchCardBalance(params.row.id);
    } else {
      setCardBalance('');
    }
  };

  // Kart bakiyesini API'den getiren fonksiyon
  const fetchCardBalance = async (cardId: number) => {
    setIsLoadingBalance(true);
    try {
      const result = await getMealCardBalance({
        cardId: cardId,
        companyId: currentUserState?.companyId || 0,
      }).unwrap();

      setCardBalance(result.data || '0.00');
    } catch (error: any) {
      setCardBalance('0.00');

      // Hata mesajını snackbar olarak göster
      const errorMessage = error?.data?.message || 'Kart bakiyesi alınırken bir hata oluştu.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsLoadingBalance(false);
    }
  };

  // Modal kapatma fonksiyonu
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCardForRefund(null);
    setCardBalance('');
    setRefundAmount('');
    setRefundAmountError('');
    setIsConfirmationOpen(false);
  };

  // Tutar formatlama yardımcıları (₺ akümülatör stili)
  const formatRefundInput = (value: string): string => {
    let numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    numericValue = numericValue.padStart(3, '0');
    const integerPart = numericValue.slice(0, -2);
    const decimalPart = numericValue.slice(-2);
    const integerFormatted = new Intl.NumberFormat('tr-TR').format(BigInt(integerPart));
    return `₺${integerFormatted},${decimalPart}`;
  };

  const parseRefundAmount = (formatted: string): number =>
    parseFloat(formatted.replace('₺', '').replaceAll('.', '').replace(',', '.')) || 0;

  // İade miktarı değişiklik fonksiyonu (formatlanmış değer alır)
  const handleRefundAmountChange = (formatted: string) => {
    setRefundAmount(formatted);
    if (formatted && cardBalance && canViewMealCardBalance) {
      const refundValue = parseRefundAmount(formatted);
      const balanceValue = parseFloat(cardBalance);
      if (refundValue === 0) {
        setRefundAmountError('Geçerli bir tutar giriniz');
      } else if (refundValue > balanceValue) {
        setRefundAmountError('Girilen tutar mevcut bakiyeden fazla olamaz!');
      } else {
        setRefundAmountError('');
      }
    } else {
      setRefundAmountError('');
    }
  };

  // İade işlemini onaylama fonksiyonu
  const handleConfirmRefund = () => {
    // Zorunlu alan kontrolü
    if (!refundAmount || parseRefundAmount(refundAmount) === 0) {
      setRefundAmountError('Bu alan zorunludur.');
      return;
    }

    // Bakiye kontrolü
    if (refundAmountError) {
      return;
    }

    // Onay popup'ını aç
    setIsConfirmationOpen(true);
  };

  // Onay popup'ındaki onayla butonuna basıldığında çalışacak fonksiyon
  const handleConfirmRefundProcess = async () => {
    setIsRefundProcessing(true);
    setIsConfirmationOpen(false);

    try {
      const result = await mealCardRefund({
        cardId: selectedCardForRefund?.id,
        companyId: currentUserState?.companyId || 0,
        refundAmount: parseRefundAmount(refundAmount),
      }).unwrap();

      // Başarı mesajını snackbar olarak göster
      const successMessage = result?.message || 'İade işlemi başarıyla tamamlandı.';
      enqueueSnackbar(successMessage, { variant: 'success' });

      handleCloseModal();
    } catch (error: any) {

      // Hata mesajını snackbar olarak göster
      const errorMessage = error?.data?.message || 'İade işlemi sırasında bir hata oluştu.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsRefundProcessing(false);
    }
  };

  // Onay popup'ını kapatma fonksiyonu
  const handleCloseConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  const debounce = (func: any, delay: any) => {
    let timeoutId: any;
    return (...args: any) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const onFilterChange = debounce((filterObj: any) => {
    setRequestBody((prev) => ({ ...prev, filters: filterObj }));
  }, 500);

  const handleChangePagination = (model: any) => {
    setPaginationModel(model);
    setRequestBody((prev) => ({ ...prev, pageSize: model.pageSize, page: model.page + 1 }));
  };

  const sortModelChange = (model: any) => {
    setRequestBody((prev) => ({ ...prev, orderBy: model[0]?.field ?? '', orderDesc: model[0]?.sort === 'asc' }));
  };

  /*   useEffect(() => {
      const updatedColumns = initialColumns.map(column => {
        if (column.field === 'productCode' && (enumProductCodeLookup?.data ?? []).length > 0) {
          return {
            ...column,
            valueOptions: enumProductCodeLookup?.data.map((item: any) => ({
              value: item.code,
              label: item.titles,
            })),
          };
        }
        return column;
      });
      setColumns([...updatedColumns]);
    }, [enumProductCodeLookup]);
   */

  const TakeRefundModalContent = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Kart Numarası
          </Typography>
          <TextField
            fullWidth
            value={selectedCardForRefund?.cardNo || ''}
            disabled
            variant="outlined"
            size="small"
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#000',
                color: '#000',
              },
              '& .MuiInputBase-root.Mui-disabled': {
                backgroundColor: '#f5f5f5',
              },
            }}
          />
        </Box>

        {canViewMealCardBalance && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Kart Bakiyesi
            </Typography>
            <TextField
              fullWidth
              value={isLoadingBalance ? 'Yükleniyor...' : `${cardBalance} TL`}
              disabled
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#000',
                  color: '#000',
                },
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            İade Alınacak Miktar <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            value={refundAmount}
            onChange={(e) => {
              const input = e.target as HTMLInputElement;
              const digits = input.value.replace(/\D/g, '');
              if (digits.length > 16) return;
              const formatted = formatRefundInput(input.value);
              handleRefundAmountChange(formatted);
              requestAnimationFrame(() => {
                input.setSelectionRange(formatted.length, formatted.length);
              });
            }}
            onFocus={(e) => {
              const input = e.target as HTMLInputElement;
              requestAnimationFrame(() => {
                input.setSelectionRange(input.value.length, input.value.length);
              });
            }}
            onPaste={(e) => {
              e.preventDefault();
              const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 16);
              if (!digits) return;
              const formatted = formatRefundInput(digits);
              handleRefundAmountChange(formatted);
              const input = e.target as HTMLInputElement;
              requestAnimationFrame(() => {
                input.setSelectionRange(formatted.length, formatted.length);
              });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                const input = e.target as HTMLInputElement;
                const value = input.value;
                const cursorPos = input.selectionStart ?? value.length;
                const selEnd = input.selectionEnd ?? cursorPos;
                const digits = value.replace(/\D/g, '');
                let newDigits: string;
                if (selEnd > cursorPos) {
                  const digitsBeforeStart = value.slice(0, cursorPos).replace(/\D/g, '').length;
                  const digitsBeforeEnd = value.slice(0, selEnd).replace(/\D/g, '').length;
                  newDigits = digits.slice(0, digitsBeforeStart) + digits.slice(digitsBeforeEnd);
                } else if (e.key === 'Backspace') {
                  let digitIndex = -1;
                  for (let i = cursorPos - 1; i >= 0; i--) {
                    if (/\d/.test(value[i])) {
                      digitIndex = value.slice(0, i).replace(/\D/g, '').length;
                      break;
                    }
                  }
                  if (digitIndex === -1) return;
                  newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
                } else {
                  let digitIndex = -1;
                  for (let i = cursorPos; i < value.length; i++) {
                    if (/\d/.test(value[i])) {
                      digitIndex = value.slice(0, i).replace(/\D/g, '').length;
                      break;
                    }
                  }
                  if (digitIndex === -1) return;
                  newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
                }
                const formatted = formatRefundInput(newDigits);
                handleRefundAmountChange(formatted);
                requestAnimationFrame(() => {
                  input.setSelectionRange(formatted.length, formatted.length);
                });
                return;
              }
              const allowedKeys = ['Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
              if (!allowedKeys.includes(e.key) && (e.key < '0' || e.key > '9') && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
              }
            }}
            placeholder="İade miktarını giriniz"
            variant="outlined"
            size="small"
            error={!!refundAmountError}
            helperText={refundAmountError}
            FormHelperTextProps={{
              sx: { fontSize: '0.9375rem' }
            }}
          />
        </Box>
      </Box>
    );
  };
  // Yetki kontrolü yüklenirken bekle
  if (isLoadingPermission) {
    return null; // veya loading spinner gösterebilirsiniz
  }

  // Eğer kart listesi görüntüleme yetkisi yoksa uyarı göster
  if (!canViewCardList) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Card
          sx={{
            maxWidth: 500,
            p: 4,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(252, 165, 165, 0.3)',
            textAlign: 'center',
          }}
        >
          <SecurityIcon sx={{ fontSize: 64, color: '#ef4444', mb: 3 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
            Erişim Engellendi
          </Typography>
          <Typography variant="body1" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
            Bu sayfaya erişim yetkiniz bulunmamaktadır. Sistem yöneticinizle iletişime geçiniz.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
      {/* İade Onay Modal */}
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        title="İade Onayı"
        isLoading={isRefundProcessing}
        confirmButtonText="Onayla"
        closeButtonText="Vazgeç"
        optionalWidth="auto"
        onConfirm={handleConfirmRefundProcess}
        children={
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              İade alınacak miktar: <strong>{refundAmount}</strong>, {currentUserState?.companyName || 'Şirket'}{' '}
              kurumsal hesabına aktarılacaktır. Onaylıyor musunuz?
            </Typography>
          </Box>
        }
      />

      {/* İade Al Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="İade Al"
        isLoading={false}
        confirmButtonText="İade Al"
        closeButtonText=""
        optionalWidth="500px"
        onConfirm={handleConfirmRefund}
        children={TakeRefundModalContent()}
      />

      <Table
        sortModelChange={sortModelChange}
        loading={isFetchLoading}
        initialState={
          {
            pinnedColumns: {
              left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'actions'],
              right: ['refund'],
            },
          } as GridInitialStatePro
        }
        sx={{
          margin:
            fetchData?.data?.length > 0 && paginationModel.pageSize > 25 ? '0px 0 25px 0 !important' : '0px !important',
          '& .MuiDataGrid-root': {
            borderRadius: 2,
            border: 'none',
          },
          '& .MuiDataGrid-main': {
            borderRadius: 2,
          },
          '& .MuiDataGrid-columnHeaders': {
            background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.95) 100%)',
            borderBottom: '2px solid rgba(226, 232, 240, 0.8)',
            borderRadius: '8px 8px 0 0',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              background: 'rgba(59, 130, 246, 0.04)',
            },
            '&.Mui-selected': {
              background: 'rgba(30, 60, 114, 0.08)',
              '&:hover': {
                background: 'rgba(30, 60, 114, 0.12)',
              },
            },
          },
        }}
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        checkboxSelection
        isMasterDetailActive={false}
        headerFilters={true}
        columnVisibilityConfigKey="mealCardListFilterColumnVisibilityKey"
        filterKey="mealCardListFilter"
        rows={fetchData?.data ?? []}
        columns={columns}
        isLoading={isFetching || isFetchLoading || (fetchData === undefined && !!requestBody?.customerId && !!canViewCardList)}
        handleFilterChange={onFilterChange}
        paginationModel={paginationModel}
        onPaginationModelChange={handleChangePagination}
        rowCount={fetchData?.totalCount ?? 0}
        getRowId={(row: any) => row.id}
        idName={'id'}
        listUrl={listUrl}
        fetchData={fetchData}
        showDownloadButton={true}
        listName="Yemek Kartı Listesi"
        requestBody={requestBody}
        fetchMethod="POST"
      />
    </>
  );
};

export default MealCardsList;
